import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Users, Package, ShoppingBag, DollarSign, Plus, Edit, Trash, Tag, Download, Store, CheckCircle2, Clock, MapPin, Printer, Wallet, Percent } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { printDeliveryLabel } from "@/lib/printLabel";
import { toast } from "sonner";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { token, user, loading: authLoading } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [orders, setOrders] = useState([]);
  const [categories, setCategories] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [cities, setCities] = useState([]);
  const [newCityName, setNewCityName] = useState("");
  const [settlements, setSettlements] = useState([]);
  const [commission, setCommission] = useState(0);
  const [commissionInput, setCommissionInput] = useState("");
  const [settlementFilter, setSettlementFilter] = useState("all"); // all | pending
  const [payingVendor, setPayingVendor] = useState(null); // vendor row for settlement modal
  const [payAmount, setPayAmount] = useState("");
  const [payNote, setPayNote] = useState("");
  const [loading, setLoading] = useState(true);
  const [showAddCategory, setShowAddCategory] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryForm, setCategoryForm] = useState({ name: "", description: "", image: "" });
  const [uploadingCategoryImage, setUploadingCategoryImage] = useState(false);

  useEffect(() => {
    // Wait for auth loading to complete
    if (authLoading) return;
    
    if (!user || user.role !== "admin") {
      toast.error("Admin access required");
      navigate("/");
      return;
    }
    fetchData();
  }, [user, authLoading]);

  const fetchData = async () => {
    try {
      const [statsRes, usersRes, ordersRes, categoriesRes, vendorsRes, citiesRes, settingsRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/users`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/orders`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/categories`),
        axios.get(`${API}/admin/vendors`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/cities`),
        axios.get(`${API}/admin/settings`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data);
      setOrders(ordersRes.data);
      setCategories(categoriesRes.data);
      setVendors(vendorsRes.data);
      setCities(citiesRes.data);
      setCommission(settingsRes.data.commission_percent);
      setCommissionInput(String(settingsRes.data.commission_percent));
      await fetchSettlements("all");
    } catch (error) {
      console.error("Failed to fetch admin data:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSettlements = async (filter) => {
    try {
      const url = filter === "pending" ? `${API}/admin/settlements?status=pending` : `${API}/admin/settlements`;
      const res = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
      setSettlements(res.data);
    } catch (error) {
      console.error("Failed to fetch settlements:", error);
    }
  };

  const handleSettlementFilter = (filter) => {
    setSettlementFilter(filter);
    fetchSettlements(filter);
  };

  const handleSaveCommission = async () => {
    const pct = parseFloat(commissionInput);
    if (isNaN(pct) || pct < 0 || pct > 100) {
      toast.error("Enter a commission between 0 and 100");
      return;
    }
    try {
      await axios.put(`${API}/admin/settings`, { commission_percent: pct }, { headers: { Authorization: `Bearer ${token}` } });
      setCommission(pct);
      toast.success("Commission updated");
      fetchSettlements(settlementFilter);
    } catch (error) {
      toast.error("Failed to update commission");
    }
  };

  const openSettlementModal = (vendor) => {
    setPayingVendor(vendor);
    setPayAmount(vendor.pending > 0 ? String(vendor.pending) : "");
    setPayNote("");
  };

  const handleRecordSettlement = async (e) => {
    e.preventDefault();
    const amt = parseFloat(payAmount);
    if (isNaN(amt) || amt <= 0) {
      toast.error("Enter a valid amount");
      return;
    }
    try {
      await axios.post(`${API}/admin/settlements`, { vendor_id: payingVendor.vendor_id, amount: amt, note: payNote }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Settlement recorded");
      setPayingVendor(null);
      fetchSettlements(settlementFilter);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to record settlement");
    }
  };

  const handleUpdateOrderStatus = async (orderId, status) => {
    try {
      await axios.put(`${API}/admin/orders/${orderId}/status`, { status }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Order marked ${status}`);
      fetchData();
    } catch (error) {
      toast.error("Failed to update order status");
    }
  };

  const handlePrintLabel = (order) => {
    printDeliveryLabel({
      orderId: order.id,
      customerName: order.customer?.name || order.shipping_address?.name,
      customerPhone: order.customer?.phone || order.shipping_address?.phone,
      address: order.shipping_address,
      items: order.items,
      vendorName: "OrgShop",
    });
  };

  const handleAddCategory = async (e) => {
    e.preventDefault();
    try {
      if (editingCategory) {
        // Update existing category
        await axios.put(`${API}/categories/${editingCategory.id}`, categoryForm, { headers: { Authorization: `Bearer ${token}` } });
        toast.success("Category updated successfully");
      } else {
        // Add new category
        await axios.post(`${API}/categories`, categoryForm, { headers: { Authorization: `Bearer ${token}` } });
        toast.success("Category added successfully");
      }
      setShowAddCategory(false);
      setEditingCategory(null);
      setCategoryForm({ name: "", description: "", image: "" });
      fetchData();
    } catch (error) {
      toast.error(editingCategory ? "Failed to update category" : "Failed to add category");
    }
  };

  const handleEditCategory = (category) => {
    setEditingCategory(category);
    setCategoryForm({
      name: category.name,
      description: category.description || "",
      image: category.image || "",
    });
    setShowAddCategory(true);
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm("Are you sure you want to delete this category? This may affect products using this category.")) {
      return;
    }
    try {
      await axios.delete(`${API}/categories/${categoryId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Category deleted successfully");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete category");
    }
  };

  const handleCancelEdit = () => {
    setShowAddCategory(false);
    setEditingCategory(null);
    setCategoryForm({ name: "", description: "", image: "" });
  };

  const handleExportCSV = async (vendorId = null) => {
    try {
      const url = vendorId 
        ? `${API}/admin/orders/export?vendor_id=${vendorId}`
        : `${API}/admin/orders/export`;
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      // Create download link
      const blob = new Blob([response.data], { type: 'text/csv' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `orders_export_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
      
      toast.success("Orders exported successfully!");
    } catch (error) {
      toast.error("Failed to export orders");
    }
  };

  const handleCategoryImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingCategoryImage(true);
    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await axios.post(`${API}/upload-image`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });

      const imageUrl = `${process.env.REACT_APP_BACKEND_URL}${response.data.url}`;
      setCategoryForm({ ...categoryForm, image: imageUrl });
      toast.success("Image uploaded successfully!");
    } catch (error) {
      toast.error("Failed to upload image");
    } finally {
      setUploadingCategoryImage(false);
    }
  };

  const handleApproveVendor = async (vendorId) => {    try {
      await axios.put(`${API}/admin/vendors/${vendorId}/approve`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Vendor approved");
      fetchData();
    } catch (error) {
      toast.error("Failed to approve vendor");
    }
  };

  const handleSuspendVendor = async (vendorId) => {
    try {
      await axios.put(`${API}/admin/vendors/${vendorId}/suspend`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Vendor moved to pending");
      fetchData();
    } catch (error) {
      toast.error("Failed to update vendor");
    }
  };

  const handleAddCity = async (e) => {
    e.preventDefault();
    if (!newCityName.trim()) return;
    try {
      await axios.post(`${API}/admin/cities`, { name: newCityName.trim() }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("City added");
      setNewCityName("");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add city");
    }
  };

  const handleDeleteCity = async (cityId) => {
    try {
      await axios.delete(`${API}/admin/cities/${cityId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("City deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete city");
    }
  };

  if (loading) {
    return (
      <div data-testid="admin-dashboard-loading">
        <Navbar />
        <div className="min-h-screen flex items-center justify-center">
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard">
      <Navbar />

      {/* Record Settlement Modal */}
      {payingVendor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="admin-settlement-modal">
          <div className="bg-card rounded-2xl p-6 w-full max-w-md shadow-hover">
            <h3 className="font-heading text-2xl font-bold mb-1">Record Payout</h3>
            <p className="text-sm text-muted-foreground mb-4">{payingVendor.name || payingVendor.phone}</p>
            <div className="bg-secondary/40 rounded-xl p-4 mb-4 text-sm space-y-1">
              <div className="flex justify-between"><span className="text-muted-foreground">Net Payable</span><span className="font-semibold">₹{payingVendor.net_payable.toFixed(2)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Already Settled</span><span className="font-semibold">₹{payingVendor.total_settled.toFixed(2)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Pending</span><span className="font-bold text-orange-600">₹{payingVendor.pending.toFixed(2)}</span></div>
              {payingVendor.payment_details?.gpay && <div className="flex justify-between"><span className="text-muted-foreground">UPI/GPay</span><span className="font-medium">{payingVendor.payment_details.gpay}</span></div>}
              {payingVendor.payment_details?.bank_account && <div className="flex justify-between"><span className="text-muted-foreground">Bank A/C</span><span className="font-medium">{payingVendor.payment_details.bank_account}</span></div>}
              {payingVendor.payment_details?.ifsc && <div className="flex justify-between"><span className="text-muted-foreground">IFSC</span><span className="font-medium">{payingVendor.payment_details.ifsc}</span></div>}
            </div>
            <form onSubmit={handleRecordSettlement} className="space-y-3">
              <div>
                <label className="block text-sm font-medium mb-1">Amount (₹)</label>
                <Input type="number" step="0.01" value={payAmount} onChange={(e) => setPayAmount(e.target.value)} required data-testid="admin-settlement-amount-input" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Note / Reference (optional)</label>
                <Input value={payNote} onChange={(e) => setPayNote(e.target.value)} placeholder="e.g. UPI txn ref" data-testid="admin-settlement-note-input" />
              </div>
              {payingVendor.settlements?.length > 0 && (
                <div className="max-h-28 overflow-y-auto text-xs text-muted-foreground border-t border-border pt-2">
                  <p className="font-medium text-foreground mb-1">Past payouts</p>
                  {payingVendor.settlements.map((p) => (
                    <div key={p.id} className="flex justify-between">
                      <span>{new Date(p.created_at).toLocaleDateString()} {p.note ? `· ${p.note}` : ""}</span>
                      <span>₹{p.amount.toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              )}
              <div className="flex gap-2 pt-2">
                <Button type="submit" className="flex-1 rounded-full" data-testid="admin-settlement-submit-btn">Record</Button>
                <Button type="button" variant="outline" onClick={() => setPayingVendor(null)} className="flex-1 rounded-full" data-testid="admin-settlement-cancel-btn">Cancel</Button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="min-h-screen py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center mb-8">
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-primary" data-testid="admin-dashboard-title">
              Admin Dashboard
            </h1>
            <Button
              onClick={() => setShowAddCategory(!showAddCategory)}
              className="bg-accent text-accent-foreground hover:bg-accent/90 rounded-full"
              data-testid="admin-add-category-btn"
            >
              <Plus className="w-5 h-5 mr-2" />
              Add Category
            </Button>
          </div>

          {/* Add Category Form */}
          {showAddCategory && (
            <div className="bg-card rounded-2xl p-6 shadow-soft mb-8" data-testid="admin-add-category-form">
              <h2 className="font-heading text-2xl font-bold mb-6">
                {editingCategory ? "Edit Category" : "Add New Category"}
              </h2>
              <form onSubmit={handleAddCategory} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Category Name</label>
                    <Input
                      value={categoryForm.name}
                      onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                      required
                      data-testid="admin-category-name-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Category Image</label>
                    <div className="flex items-center space-x-4">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleCategoryImageUpload}
                        className="hidden"
                        id="category-image-upload"
                      />
                      <label
                        htmlFor="category-image-upload"
                        className="px-4 py-2 bg-primary text-primary-foreground rounded-lg cursor-pointer hover:bg-primary/90"
                      >
                        {uploadingCategoryImage ? "Uploading..." : "Upload Image"}
                      </label>
                      {categoryForm.image && (
                        <div className="flex items-center space-x-2">
                          <img src={categoryForm.image} alt="Preview" className="w-12 h-12 object-cover rounded-lg" />
                          <span className="text-sm text-green-600">✓ Image set</span>
                        </div>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">Or paste image URL below</p>
                    <Input
                      value={categoryForm.image}
                      onChange={(e) => setCategoryForm({ ...categoryForm, image: e.target.value })}
                      placeholder="https://example.com/image.jpg"
                      className="mt-2"
                      data-testid="admin-category-image-input"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Description (Optional)</label>
                  <Input
                    value={categoryForm.description}
                    onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })}
                    data-testid="admin-category-description-input"
                  />
                </div>
                <div className="flex space-x-4">
                  <Button type="submit" className="flex-1 rounded-full" data-testid="admin-category-submit-btn">
                    {editingCategory ? "Update Category" : "Add Category"}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleCancelEdit}
                    className="flex-1 rounded-full"
                    data-testid="admin-category-cancel-btn"
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </div>
          )}

          {/* Stats Cards */}
          {stats && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8" data-testid="admin-stats-section">
              <div className="bg-card rounded-2xl p-6 shadow-soft">
                <div className="flex items-center justify-between mb-4">
                  <Users className="w-10 h-10 text-primary" />
                  <span className="text-xs font-medium text-muted-foreground">Total Users</span>
                </div>
                <p className="font-heading text-4xl font-bold text-primary" data-testid="admin-stat-users">{stats.total_users}</p>
              </div>
              <div className="bg-card rounded-2xl p-6 shadow-soft">
                <div className="flex items-center justify-between mb-4">
                  <Users className="w-10 h-10 text-accent" />
                  <span className="text-xs font-medium text-muted-foreground">Total Vendors</span>
                </div>
                <p className="font-heading text-4xl font-bold text-accent" data-testid="admin-stat-vendors">{stats.total_vendors}</p>
              </div>
              <div className="bg-card rounded-2xl p-6 shadow-soft">
                <div className="flex items-center justify-between mb-4">
                  <Package className="w-10 h-10 text-primary" />
                  <span className="text-xs font-medium text-muted-foreground">Total Products</span>
                </div>
                <p className="font-heading text-4xl font-bold text-primary" data-testid="admin-stat-products">{stats.total_products}</p>
              </div>
              <div className="bg-card rounded-2xl p-6 shadow-soft">
                <div className="flex items-center justify-between mb-4">
                  <ShoppingBag className="w-10 h-10 text-accent" />
                  <span className="text-xs font-medium text-muted-foreground">Total Orders</span>
                </div>
                <p className="font-heading text-4xl font-bold text-accent" data-testid="admin-stat-orders">{stats.total_orders}</p>
              </div>
            </div>
          )}

          {/* Revenue Card */}
          {stats && (
            <div className="bg-gradient-to-r from-primary to-primary/80 text-primary-foreground rounded-2xl p-8 shadow-hover mb-8" data-testid="admin-revenue-section">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-primary-foreground/80 mb-2">Total Revenue</p>
                  <p className="font-heading text-5xl font-bold" data-testid="admin-stat-revenue">₹{stats.total_revenue.toFixed(2)}</p>
                </div>
                <DollarSign className="w-20 h-20 text-primary-foreground/20" />
              </div>
            </div>
          )}

          {/* Cities Management */}
          <div className="bg-card rounded-2xl p-6 shadow-soft mb-8" data-testid="admin-cities-section">
            <h2 className="font-heading text-2xl font-bold mb-6 flex items-center">
              <MapPin className="w-6 h-6 mr-2 text-primary" />
              Delivery Cities
            </h2>
            <form onSubmit={handleAddCity} className="flex gap-2 mb-4">
              <Input
                value={newCityName}
                onChange={(e) => setNewCityName(e.target.value)}
                placeholder="Add a city (e.g. Salem)"
                data-testid="admin-city-input"
              />
              <Button type="submit" className="rounded-full" data-testid="admin-add-city-btn">
                <Plus className="w-4 h-4 mr-1" /> Add
              </Button>
            </form>
            <div className="flex flex-wrap gap-2" data-testid="admin-cities-list">
              {cities.map((c) => (
                <span key={c.id} className="flex items-center gap-2 px-4 py-1.5 bg-primary/10 text-primary rounded-full text-sm" data-testid="admin-city-chip">
                  {c.name}
                  <button
                    onClick={() => handleDeleteCity(c.id)}
                    className="hover:text-red-500"
                    data-testid={`admin-delete-city-${c.name}`}
                  >
                    <Trash className="w-3.5 h-3.5" />
                  </button>
                </span>
              ))}
              {cities.length === 0 && <p className="text-muted-foreground text-sm">No cities yet. Add one above.</p>}
            </div>
          </div>

          {/* Vendor Approvals */}
          <div className="bg-card rounded-2xl p-6 shadow-soft mb-8" data-testid="admin-vendors-section">
            <h2 className="font-heading text-2xl font-bold mb-6 flex items-center">
              <Store className="w-6 h-6 mr-2 text-primary" />
              Vendor Approvals
              {vendors.filter((v) => v.vendor_status === "pending").length > 0 && (
                <span className="ml-3 bg-yellow-100 text-yellow-800 text-sm font-medium px-3 py-1 rounded-full" data-testid="admin-pending-vendor-count">
                  {vendors.filter((v) => v.vendor_status === "pending").length} pending
                </span>
              )}
            </h2>
            {vendors.length === 0 ? (
              <div className="text-center py-12">
                <Store className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No vendors yet</p>
              </div>
            ) : (
              <div className="space-y-3" data-testid="admin-vendors-list">
                {vendors.map((vendor) => {
                  const pending = vendor.vendor_status === "pending";
                  return (
                    <div key={vendor.id} className="flex items-center justify-between border border-border rounded-xl p-4" data-testid="admin-vendor-card">
                      <div>
                        <p className="font-semibold" data-testid="admin-vendor-name">{vendor.name || "Unnamed Vendor"}</p>
                        <p className="text-sm text-muted-foreground">{vendor.phone}</p>
                        {vendor.location?.district && (
                          <p className="text-xs text-muted-foreground">{vendor.location.city}, {vendor.location.district}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-3">
                        <span
                          className={`px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${pending ? "bg-yellow-100 text-yellow-800" : "bg-green-100 text-green-800"}`}
                          data-testid="admin-vendor-status"
                        >
                          {pending ? <Clock className="w-3 h-3" /> : <CheckCircle2 className="w-3 h-3" />}
                          {pending ? "Pending" : "Approved"}
                        </span>
                        {pending ? (
                          <Button
                            onClick={() => handleApproveVendor(vendor.id)}
                            size="sm"
                            className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
                            data-testid="admin-approve-vendor-btn"
                          >
                            <CheckCircle2 className="w-4 h-4 mr-1" />
                            Approve
                          </Button>
                        ) : (
                          <Button
                            onClick={() => handleSuspendVendor(vendor.id)}
                            size="sm"
                            variant="outline"
                            className="rounded-full text-yellow-600 hover:bg-yellow-50"
                            data-testid="admin-suspend-vendor-btn"
                          >
                            Suspend
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Vendor Settlements */}
          <div className="bg-card rounded-2xl p-6 shadow-soft mb-8" data-testid="admin-settlements-section">
            <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
              <h2 className="font-heading text-2xl font-bold flex items-center">
                <Wallet className="w-6 h-6 mr-2 text-primary" />
                Vendor Settlements
              </h2>
              <div className="flex items-center gap-2 bg-secondary/40 rounded-full px-3 py-1.5" data-testid="admin-commission-control">
                <Percent className="w-4 h-4 text-primary" />
                <span className="text-sm text-muted-foreground">Commission</span>
                <Input
                  type="number"
                  value={commissionInput}
                  onChange={(e) => setCommissionInput(e.target.value)}
                  className="w-20 h-8"
                  data-testid="admin-commission-input"
                />
                <span className="text-sm">%</span>
                <Button onClick={handleSaveCommission} size="sm" className="rounded-full h-8" data-testid="admin-commission-save-btn">
                  Save
                </Button>
              </div>
            </div>

            <div className="flex gap-2 mb-4">
              <button
                onClick={() => handleSettlementFilter("all")}
                className={`px-4 py-1.5 rounded-full text-sm border transition-colors ${settlementFilter === "all" ? "bg-primary text-primary-foreground border-primary" : "bg-background border-border hover:border-primary"}`}
                data-testid="admin-settlement-filter-all"
              >
                All Vendors
              </button>
              <button
                onClick={() => handleSettlementFilter("pending")}
                className={`px-4 py-1.5 rounded-full text-sm border transition-colors ${settlementFilter === "pending" ? "bg-primary text-primary-foreground border-primary" : "bg-background border-border hover:border-primary"}`}
                data-testid="admin-settlement-filter-pending"
              >
                Pending Payouts
              </button>
            </div>

            {settlements.length === 0 ? (
              <div className="text-center py-12" data-testid="admin-settlements-empty">
                <Wallet className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">
                  {settlementFilter === "pending" ? "No pending payouts 🎉" : "No vendors with sales yet"}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto" data-testid="admin-settlements-list">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-muted-foreground border-b border-border">
                      <th className="py-2 pr-3 font-medium">Vendor</th>
                      <th className="py-2 px-3 font-medium text-right">Gross</th>
                      <th className="py-2 px-3 font-medium text-right">Commission ({commission}%)</th>
                      <th className="py-2 px-3 font-medium text-right">Net Payable</th>
                      <th className="py-2 px-3 font-medium text-right">Settled</th>
                      <th className="py-2 px-3 font-medium text-right">Pending</th>
                      <th className="py-2 pl-3 font-medium text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {settlements.map((s) => (
                      <tr key={s.vendor_id} className="border-b border-border/60" data-testid="admin-settlement-row">
                        <td className="py-3 pr-3">
                          <p className="font-semibold" data-testid="admin-settlement-vendor">{s.name || "Unnamed"}</p>
                          <p className="text-xs text-muted-foreground">{s.phone} · {s.order_count} orders</p>
                        </td>
                        <td className="py-3 px-3 text-right">₹{s.gross_sales.toFixed(2)}</td>
                        <td className="py-3 px-3 text-right text-muted-foreground">-₹{s.commission_amount.toFixed(2)}</td>
                        <td className="py-3 px-3 text-right font-semibold">₹{s.net_payable.toFixed(2)}</td>
                        <td className="py-3 px-3 text-right text-green-700">₹{s.total_settled.toFixed(2)}</td>
                        <td className="py-3 px-3 text-right">
                          <span className={`font-bold ${s.pending > 0.009 ? "text-orange-600" : "text-green-700"}`} data-testid="admin-settlement-pending">
                            ₹{s.pending.toFixed(2)}
                          </span>
                        </td>
                        <td className="py-3 pl-3 text-right">
                          <Button
                            onClick={() => openSettlementModal(s)}
                            size="sm"
                            variant="outline"
                            className="rounded-full"
                            disabled={s.net_payable <= 0}
                            data-testid="admin-settle-btn"
                          >
                            Record Payout
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Categories Management */}
          <div className="bg-card rounded-2xl p-6 shadow-soft mb-8 mt-8" data-testid="admin-categories-section">
            <h2 className="font-heading text-2xl font-bold mb-6 flex items-center">
              <Tag className="w-6 h-6 mr-2 text-primary" />
              Manage Categories
            </h2>
            {categories.length === 0 ? (
              <div className="text-center py-12">
                <Tag className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No categories yet. Add your first category!</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {categories.map((category) => (
                  <div key={category.id} className="border border-border rounded-xl p-4 hover:shadow-soft transition-all" data-testid="admin-category-card">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <h3 className="font-semibold text-lg mb-1">{category.name}</h3>
                        {category.description && (
                          <p className="text-sm text-muted-foreground line-clamp-2">{category.description}</p>
                        )}
                      </div>
                      {category.image && (
                        <div className="w-12 h-12 rounded-lg overflow-hidden bg-muted ml-3 flex-shrink-0">
                          <img src={category.image} alt={category.name} className="w-full h-full object-cover" />
                        </div>
                      )}
                    </div>
                    <div className="flex space-x-2 pt-3 border-t border-border">
                      <Button
                        onClick={() => handleEditCategory(category)}
                        variant="outline"
                        size="sm"
                        className="flex-1 rounded-full"
                        data-testid="admin-category-edit-btn"
                      >
                        <Edit className="w-4 h-4 mr-1" />
                        Edit
                      </Button>
                      <Button
                        onClick={() => handleDeleteCategory(category.id)}
                        variant="outline"
                        size="sm"
                        className="flex-1 rounded-full text-red-500 hover:text-red-600 hover:bg-red-50"
                        data-testid="admin-category-delete-btn"
                      >
                        <Trash className="w-4 h-4 mr-1" />
                        Delete
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Recent Orders */}
            <div className="bg-card rounded-2xl p-6 shadow-soft" data-testid="admin-orders-section">
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-heading text-2xl font-bold">Recent Orders</h2>
                <Button
                  onClick={() => handleExportCSV()}
                  variant="outline"
                  className="rounded-full"
                  data-testid="admin-export-orders-btn"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Export CSV
                </Button>
              </div>
              {orders.length === 0 ? (
                <div className="text-center py-12" data-testid="admin-orders-empty">
                  <ShoppingBag className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">No orders yet</p>
                </div>
              ) : (
                <div className="space-y-3 max-h-[32rem] overflow-y-auto" data-testid="admin-orders-list">
                  {orders.slice(0, 15).map((order) => {
                    const ADMIN_STATUSES = ["confirmed", "processing", "shipped", "delivered", "cancelled"];
                    const addr = order.shipping_address;
                    return (
                      <div key={order.id} className="border border-border rounded-lg p-3" data-testid="admin-order-card">
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <p className="font-semibold text-sm" data-testid="admin-order-id">Order #{order.id.slice(0, 8)}</p>
                            <p className="text-xs text-muted-foreground">{new Date(order.created_at).toLocaleDateString()}</p>
                          </div>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${order.status === 'pending' ? 'bg-yellow-100 text-yellow-800' : order.status === 'delivered' ? 'bg-green-100 text-green-800' : order.status === 'cancelled' || order.status === 'failed' ? 'bg-red-100 text-red-800' : 'bg-blue-100 text-blue-800'}`} data-testid="admin-order-status">
                            {order.status}
                          </span>
                        </div>
                        <div className="text-xs text-muted-foreground mb-2" data-testid="admin-order-customer">
                          <span className="font-medium text-foreground">{order.customer?.name || addr?.name || "Customer"}</span>
                          {(order.customer?.phone || addr?.phone) && <span> · {order.customer?.phone || addr?.phone}</span>}
                          {addr && <div>{[addr.address_line1, addr.city, addr.pincode].filter(Boolean).join(", ")}</div>}
                        </div>
                        <p className="font-heading text-lg font-bold text-primary mb-2" data-testid="admin-order-amount">₹{order.total_amount.toFixed(2)}</p>
                        <div className="flex flex-wrap items-center gap-1.5 pt-2 border-t border-border">
                          {ADMIN_STATUSES.map((s) => (
                            <button
                              key={s}
                              onClick={() => handleUpdateOrderStatus(order.id, s)}
                              disabled={order.status === s}
                              className={`px-2 py-0.5 rounded-full text-[11px] font-medium border capitalize transition-colors disabled:opacity-100 ${order.status === s ? "bg-primary text-primary-foreground border-primary" : "bg-background border-border hover:border-primary"}`}
                              data-testid={`admin-order-status-${s}`}
                            >
                              {s}
                            </button>
                          ))}
                          <Button onClick={() => handlePrintLabel(order)} variant="outline" size="sm" className="rounded-full ml-auto h-7 px-3" data-testid="admin-print-label-btn">
                            <Printer className="w-3.5 h-3.5 mr-1" /> Label
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Recent Users */}
            <div className="bg-card rounded-2xl p-6 shadow-soft" data-testid="admin-users-section">
              <h2 className="font-heading text-2xl font-bold mb-6">Recent Users</h2>
              {users.length === 0 ? (
                <div className="text-center py-12" data-testid="admin-users-empty">
                  <Users className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">No users yet</p>
                </div>
              ) : (
                <div className="space-y-3 max-h-96 overflow-y-auto" data-testid="admin-users-list">
                  {users.slice(0, 10).map((user) => (
                    <div key={user.id} className="border border-border rounded-lg p-3" data-testid="admin-user-card">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-semibold" data-testid="admin-user-name">{user.name || "No name"}</p>
                          <p className="text-sm text-muted-foreground">{user.phone}</p>
                          {user.email && <p className="text-xs text-muted-foreground">{user.email}</p>}
                        </div>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${user.role === 'admin' ? 'bg-red-100 text-red-800' : user.role === 'vendor' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'}`} data-testid="admin-user-role">
                          {user.role}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
